import { __ } from "@wordpress/i18n";
import { useBlockProps, InnerBlocks, RichText } from "@wordpress/block-editor";
import { useState, useEffect } from "@wordpress/element";
import ModalInspectorControls from "./InspectorControls";

const MODAL_TEMPLATE = [
  [
    "core/heading",
    {
      content: "Engage Your Visitors!",
      align: "center",
      placeholder: "Enter heading...",
      className: "has-text-align-center",
    },
  ],
  [
    "core/paragraph",
    {
      content:
        "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut elit tellus, luctus nec ullamcorper mattis pulvinar dapibus.",
      align: "center",
      placeholder: "Enter description...",
      className: "has-text-align-center",
    },
  ],
  [
    "core/buttons",
    {
      text: "Call To Action",
      align: "center",
      className: "has-text-align-center is-style-inline",
      style: {
        spacing: {
          margin: {
            top: "20px",
          },
        },
      },
      layout: {
        type: "flex",
        justifyContent: "center",
      },
    },
    [
      [
        "core/button",
        {
          text: "Call To Action",
          align: "center",
        },
      ],
    ],
  ],
];

const BrandyModalButtonAppender = () => (
  <div className="brandy-modal-button-appender-wrapper">
    <InnerBlocks.ButtonBlockAppender />
  </div>
);

export default function Edit({
  attributes,
  setAttributes,
  className,
  clientId,
}) {
  const blockProps = useBlockProps({
    className: "modal-trigger-button wp-element-button alignfull",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    buttonText = "Click Here",
    buttonAlign = "left",
    backdropColor = "#00000080",
    enableCloseIcon = true,
    closeOnBackdropClick = true,
    closeOnEsc = true,
    popupStyles = {},
    closeStyles = {},
    modalPosition = "center",
    triggerType = "click",
  } = attributes;

  // Auto-generate modalId on mount if empty.
  useEffect(() => {
    if (!attributes.modalId) {
      setAttributes({ modalId: `modal-${clientId.slice(0, 8)}` });
    }
  }, []);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleBackdropClick = (e) => {
    if (e.target.classList.contains("modal-backdrop")) {
      if (closeOnBackdropClick) {
        closeModal();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      if (closeOnEsc) {
        closeModal();
      }
    }
  };

  const showModal = isModalOpen;

  return (
    <div className="brandy-modal-wrapper">
      {triggerType === "click" ? (
        <div {...blockProps}>
          <RichText
            value={buttonText}
            onChange={(value) => setAttributes({ buttonText: value })}
            placeholder={__("Click Here", "brandy-blocks")}
            onClick={openModal}
          />
        </div>
      ) : (
        <div
          style={{
            padding: "10px 16px",
            background: "#f0f0f0",
            borderRadius: "4px",
            fontSize: "13px",
            color: "#757575",
            textAlign: "center",
            cursor: "pointer",
          }}
          onClick={openModal}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && openModal()}
        >
          {triggerType === "timer"
            ? __("Timer-triggered modal — click to edit content", "brandy-blocks")
            : __("Scroll-triggered modal — click to edit content", "brandy-blocks")}
        </div>
      )}

      <ModalInspectorControls
        attributes={attributes}
        setAttributes={setAttributes}
      />

      {showModal && (
        <div
          className={`modal-backdrop modal-position-${modalPosition}`}
          style={{ backgroundColor: backdropColor }}
          onClick={handleBackdropClick}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          data-modal={clientId}
        >
          <div
            className="modal-wrapper"
            style={{
              width: `${popupStyles.modalWidth || 600}${
                popupStyles.modalWidthUnit || "px"
              }`,
              maxHeight: `${popupStyles.modalMaxHeight || 90}${
                popupStyles.modalMaxHeightUnit || "vh"
              }`,
            }}
          >
            <div
              className="modal-content"
              style={{
                width: "100%",
                maxHeight: `${popupStyles.modalMaxHeight || 90}${
                  popupStyles.modalMaxHeightUnit || "vh"
                }`,
                overflowY: "auto",
                paddingTop: popupStyles.padding?.top || "20px",
                paddingRight: popupStyles.padding?.right || "20px",
                paddingBottom: popupStyles.padding?.bottom || "20px",
                paddingLeft: popupStyles.padding?.left || "20px",
                border: `${popupStyles.borderWidth || "0px"} ${
                  popupStyles.borderStyle || "solid"
                } ${popupStyles.borderColor || "#000000"}`,
                boxShadow: popupStyles.shadow || "",
              }}
            >
              <div
                className="modal-inner-content"
                style={{ maxWidth: "fit-content", margin: "0 auto" }}
              >
                <InnerBlocks
                  template={MODAL_TEMPLATE}
                  renderAppender={() => <BrandyModalButtonAppender />}
                />
              </div>
            </div>
            {enableCloseIcon && (
              <button
                className={`modal-close-button ${
                  closeStyles.closeIconPosition || "top-right"
                }`}
                onClick={closeModal}
                style={{
                  width: `${closeStyles.closeIconSize || 24}px`,
                  height: `${closeStyles.closeIconSize || 24}px`,
                }}
              >
                <svg
                  width="20"
                  height="20"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 320 512"
                >
                  <path
                    fill={closeStyles.closeIconColor || "#ffffff"}
                    d="M310.6 361.4c12.5 12.5 12.5 32.75 0 45.25C304.4 412.9 296.2 416 288 416s-16.38-3.125-22.62-9.375L160 301.3L54.63 406.6C48.38 412.9 40.19 416 32 416S15.63 412.9 9.375 406.6c-12.5-12.5-12.5-32.75 0-45.25l105.4-105.4L9.375 150.6c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L160 210.8l105.4-105.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-105.4 105.4L310.6 361.4z"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
